// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import type {
  YubiKeyInfo,
  GenerateKeyRequest,
  GenerateKeyResponse,
  StoreCertificateRequest,
  StoreCertificateResponse,
} from "@yubikey-enrollment/shared-types";

export class LocalAgentError extends Error {
  readonly errorCode: string;
  readonly retriesRemaining?: number;

  constructor(message: string, errorCode: string, retriesRemaining?: number) {
    super(message);
    this.name = "LocalAgentError";
    this.errorCode = errorCode;
    this.retriesRemaining = retriesRemaining;
  }
}

export interface ILocalAgentClient {
  checkHealth(): Promise<{ status: string; version: string }>;
  getYubiKeyInfo(): Promise<YubiKeyInfo>;
  generateKey(req: GenerateKeyRequest): Promise<GenerateKeyResponse>;
  storeCertificate(req: StoreCertificateRequest): Promise<StoreCertificateResponse>;
}

const DEFAULT_BASE_URL = "http://127.0.0.1:8880";

export class LocalAgentClient implements ILocalAgentClient {
  private readonly baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl ??
      ((typeof process !== "undefined" && process.env?.VITE_LOCAL_AGENT_URL) ||
        DEFAULT_BASE_URL);
  }

  async checkHealth(): Promise<{ status: string; version: string }> {
    return this.get<{ status: string; version: string }>("/health");
  }

  async getYubiKeyInfo(): Promise<YubiKeyInfo> {
    return this.get<YubiKeyInfo>("/yubikey/info");
  }

  async generateKey(req: GenerateKeyRequest): Promise<GenerateKeyResponse> {
    return this.post<GenerateKeyResponse>("/yubikey/generate-key", req);
  }

  async storeCertificate(req: StoreCertificateRequest): Promise<StoreCertificateResponse> {
    return this.post<StoreCertificateResponse>("/yubikey/store-certificate", req);
  }

  private async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "GET" });
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    // Fetch a single-use CSRF token before each POST
    const csrfToken = await this.fetchCsrfToken();
    return this.request<T>(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(body),
    });
  }

  private async fetchCsrfToken(): Promise<string> {
    const resp = await fetch(`${this.baseUrl}/csrf-token`);
    if (!resp.ok) {
      throw new Error("Failed to fetch CSRF token from the Local Agent.");
    }
    const data = (await resp.json()) as { csrf_token: string };
    return data.csrf_token;
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch (err) {
      throw new Error(
        `Unable to reach the Local Agent at ${this.baseUrl}. Please ensure the agent is running.`
      );
    }

    if (!response.ok) {
      let message = `Local Agent returned ${response.status}`;
      let errorCode = "UNKNOWN";
      let retriesRemaining: number | undefined;
      try {
        const errorBody = (await response.json()) as {
          message?: string;
          error_code?: string;
          retries_remaining?: number;
        };
        if (errorBody.message) {
          message = errorBody.message;
        }
        if (errorBody.error_code) {
          errorCode = errorBody.error_code;
        }
        if (errorBody.retries_remaining !== undefined) {
          retriesRemaining = errorBody.retries_remaining;
        }
      } catch {
        // response body wasn't JSON — use the default message
      }
      throw new LocalAgentError(message, errorCode, retriesRemaining);
    }

    return (await response.json()) as T;
  }
}
